import React, { ReactNode, useState } from 'react';
import ErrorIcon from '~/assets/icons/ontario-icon-alert-error.svg';
import InfoIcon from '~/assets/icons/ontario-icon-alert-information.svg';
import SuccessIcon from '~/assets/icons/ontario-icon-alert-success.svg';
import WarningIcon from '~/assets/icons/ontario-icon-alert-warning.svg';
import {
    AlertContainer,
    AlertHeadingContainer,
    AlertHeadingIcon,
    AlertBody,
    AlertHeading,
    DismissButton,
} from './styles';
import CloseIcon from '~/assets/icons/ontario-icon-close.svg';

export type AlertType = 'error' | 'success' | 'warning' | 'informational';

export interface PageAlertProps {
    alertType: AlertType;
    heading?: ReactNode | string | null;
    headingAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    role?: string;
    children?: ReactNode;
    className?: string;
    dismissible?: boolean;
    onDismiss?: () => void;
    dismissAriaLabel?: string;
}

function PageAlert({
    alertType,
    heading,
    headingAs = 'h4',
    role,
    children,
    className,
    dismissible,
    onDismiss,
    dismissAriaLabel,
}: PageAlertProps) {
    const [isDismissed, setIsDismissed] = useState(false);

    const handleDismiss = () => {
        setIsDismissed(true);
        if (onDismiss) {
            onDismiss();
        }
    };

    if (isDismissed) {
        return null;
    }

    return (
        <AlertContainer
            role={role}
            id={role === 'alert' ? 'alertRef' : undefined}
            tabIndex={role === 'alert' ? -1 : undefined}
            $alertType={alertType}
            className={className}
        >
            {!!dismissible && (
                <DismissButton onClick={handleDismiss} aria-label={dismissAriaLabel || 'Dismiss'}>
                    <CloseIcon aria-hidden="true" focusable="false" />
                </DismissButton>
            )}
            <AlertHeadingContainer $dismissible={dismissible}>
                <AlertHeadingIcon>
                    {alertType === 'error' && (
                        <ErrorIcon role="presentation" aria-hidden="true" focusable="false" />
                    )}
                    {alertType === 'success' && (
                        <SuccessIcon aria-hidden="true" focusable="false" />
                    )}
                    {alertType === 'warning' && (
                        <WarningIcon aria-hidden="true" focusable="false" />
                    )}
                    {alertType === 'informational' && (
                        <InfoIcon aria-hidden="true" focusable="false" />
                    )}
                </AlertHeadingIcon>
                {heading &&
                    (typeof heading === 'string' ? (
                        <AlertHeading $styledAs={headingAs}>{heading}</AlertHeading>
                    ) : (
                        heading
                    ))}
            </AlertHeadingContainer>
            <AlertBody>{children}</AlertBody>
        </AlertContainer>
    );
}

export default PageAlert;
